import { useMemo } from 'react';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import {
  AppointmentTypeEnum,
  CategoryEnum,
  ContactTypeEnum,
  FeedbackSubIssueEnum,
  SchedulingOutcomeEnum,
  SchedulingOutcomeV2Enum,
  SentimentEnum,
  ServiceQualityFlagEnum,
} from '@frontend/api-call-intel';
import { i18next, useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { IconName } from '@frontend/icons';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { theme } from '@frontend/theme';
import { CategoryChipVariants, ChipVariants } from '@frontend/design-system';
import { FeedbackFeature, FeedbackFeatureType } from '../constants';

// Helper for translations
const t = (key: string) => i18next.t(key, { ns: 'analytics' });

//types
export type CallIntelChipVariant<T extends ChipVariants | CategoryChipVariants = ChipVariants> = Record<
  string,
  {
    variant?: T;
    variantColor?: string;
    leftIconName?: IconName;
    fullWidth?: boolean;
    textColor?: string;
    leftIconColor?: string;
    backgroundColor?: string;
    border?: string;
    borderStyle?: React.CSSProperties['borderStyle'];
  }
>;

export type DataLabelsAndColors = {
  appointmentTypes?: Record<AppointmentTypeEnum | string, string>;
  categories?: Record<CategoryEnum | string, string>;
  contactType?: Record<ContactTypeEnum | string, string>;
  schedulingOutcome?: Record<SchedulingOutcomeEnum | string, string>;
  schedulingOutcomeV2?: Record<SchedulingOutcomeV2Enum | string, string>;
  schedulingOutcomeV2Alternate?: Record<SchedulingOutcomeV2Enum | string, string>;
  sentiments?: Record<SentimentEnum | string, string>;
  sentimentsWithEmoji?: Record<SentimentEnum | string, string>;
  serviceQualityFlags?: Record<ServiceQualityFlagEnum | string, string>;
  serviceQualityLabels?: Record<ServiceQualityFlagEnum | string, string>;
  taskTypes?: Record<TaskType | string, string>;
};

export type DescriptionLabelsMapType = {
  tasks?: Record<TaskType | string, string>;
  categories?: Record<CategoryEnum | string, string>;
};

export type PageLabels = {
  serviceQualityFlags?: Record<ServiceQualityFlagEnum | string, string>;
};
type DataLabels = Omit<DataLabelsAndColors, 'contactType'>;

const DEFAULT_APPOINTMENT_TYPES: AppointmentTypeEnum[] = [
  AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN,
  AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING,
  AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC,
  AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY,
  AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET,
  AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL,
  AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS,
  AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM,
  AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION,
  AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE,
  AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES,
  AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION,
  AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP,
  AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS,
  AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER,
];

const DEFAULT_CATEGORIES: CategoryEnum[] = [
  CategoryEnum.CATEGORY_BILLING,
  CategoryEnum.CATEGORY_COMPLAINT,
  CategoryEnum.CATEGORY_EMERGENCY,
  CategoryEnum.CATEGORY_MEDICATION,
  CategoryEnum.CATEGORY_OTHER,
  CategoryEnum.CATEGORY_SCHEDULING,
  CategoryEnum.CATEGORY_UNKNOWN,
  CategoryEnum.CATEGORY_VENDOR,
];

const DEFAULT_CHART_LABEL_COLORS: DataLabelsAndColors = {
  appointmentTypes: {
    [AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: theme.colors.secondary.seaweed50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: theme.colors.critical50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: theme.colors.secondary.eggplant50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: theme.colors.rose50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: theme.colors.secondary.seaweed50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: theme.colors.neutral50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: theme.colors.rose50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: theme.colors.secondary.seaweed50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: theme.colors.secondary.eggplant50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: theme.colors.rose50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: theme.colors.secondary.eggplant50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: theme.colors.rose50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: theme.colors.neutral50,
  },

  categories: {
    [CategoryEnum.CATEGORY_BILLING]: theme.colors.success50,
    [CategoryEnum.CATEGORY_COMPLAINT]: theme.colors.warning50,
    [CategoryEnum.CATEGORY_EMERGENCY]: theme.colors.critical50,
    [CategoryEnum.CATEGORY_MEDICATION]: theme.colors.secondary.eggplant50,
    [CategoryEnum.CATEGORY_OTHER]: theme.colors.neutral50,
    [CategoryEnum.CATEGORY_SCHEDULING]: theme.colors.primary50,
    [CategoryEnum.CATEGORY_UNKNOWN]: theme.colors.neutral50,
    [CategoryEnum.CATEGORY_VENDOR]: theme.colors.neutral50,
  },

  schedulingOutcome: {
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral80,
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: theme.colors.success50,
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning40,
  },

  schedulingOutcomeV2: {
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral5,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: theme.colors.primary60,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning30,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: theme.colors.secondary.seaweed50,
  },

  serviceQualityFlags: {
    [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: theme.colors.tangerine50,
    [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: theme.colors.rose50,
    [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: theme.colors.warning50,
  },
};

const DEFAULT_DESCRIPTION_LABELS: DescriptionLabelsMapType = {
  tasks: {
    [TaskType.TYPE_BILLING]: t('Investigating billing issues, sending invoices or receipts, making payments, etc.'),
    [TaskType.TYPE_INSURANCE]: t('Verifying insurance benefits, checking coverage, submitting authorizations, etc.'),
    [TaskType.TYPE_OTHER]: t('Tasks that don’t fit into any of the above categories.'),
    [TaskType.TYPE_PATIENT_CARE]: t(
      'Checking in on treatment-related matters, sending out forms, sharing test results, etc.'
    ),
    [TaskType.TYPE_SCHEDULING]: t(
      'Scheduling appointments, calling patients back to reschedule, sending reminders, etc.'
    ),
    [TaskType.TYPE_WAITLIST]: t('Adding patients to the waitlist or ASAP list, notifying patients of openings, etc.'),
  },
};

const DEFAULT_CHART_OUTLINE_COLORS: DataLabelsAndColors = {
  appointmentTypes: {
    [AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: theme.colors.secondary.seaweed50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: theme.colors.critical50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: theme.colors.indigo70,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: theme.colors.tangerine60,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: theme.colors.secondary.eggplant50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: theme.colors.rose60,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: theme.colors.secondary.seaweed60,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: theme.colors.neutral40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: theme.colors.rose60,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: theme.colors.indigo70,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: theme.colors.secondary.seaweed50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: theme.colors.secondary.eggplant50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: theme.colors.indigo70,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: theme.colors.rose60,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: theme.colors.secondary.eggplant50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: theme.colors.rose60,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: theme.colors.indigo70,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: theme.colors.tangerine50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: theme.colors.neutral50,
  },

  categories: {
    [CategoryEnum.CATEGORY_BILLING]: theme.colors.success50,
    [CategoryEnum.CATEGORY_COMPLAINT]: theme.colors.warning40,
    [CategoryEnum.CATEGORY_EMERGENCY]: theme.colors.critical50,
    [CategoryEnum.CATEGORY_MEDICATION]: theme.colors.secondary.eggplant60,
    [CategoryEnum.CATEGORY_OTHER]: theme.colors.neutral50,
    [CategoryEnum.CATEGORY_SCHEDULING]: theme.colors.primary70,
    [CategoryEnum.CATEGORY_UNKNOWN]: theme.colors.neutral40,
    [CategoryEnum.CATEGORY_VENDOR]: theme.colors.neutral60,
  },

  sentiments: {
    [SentimentEnum.SENTIMENT_NEGATIVE]: theme.colors.critical60,
    [SentimentEnum.SENTIMENT_NEUTRAL]: theme.colors.primary40,
    [SentimentEnum.SENTIMENT_POSITIVE]: theme.colors.success50,
    [SentimentEnum.SENTIMENT_UNKNOWN]: theme.colors.neutral50,
  },

  schedulingOutcome: {
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral10,
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: theme.colors.success60,
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning40,
  },

  schedulingOutcomeV2: {
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral5,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: theme.colors.primary60,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning30,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: theme.colors.secondary.seaweed50,
  },

  serviceQualityFlags: {
    [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: theme.colors.tangerine50,
    [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: theme.colors.rose40,
    [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: theme.colors.warning40,
  },
};

const DEFAULT_CHIP_VARIANTS: CallIntelChipVariant = {
  [AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: {
    variant: 'neutral',
  },

  // Dental-specific
  [AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: {
    variantColor: theme.colors.tangerine40,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: {
    variantColor: theme.colors.secondary.seaweed30,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: {
    variantColor: theme.colors.secondary.eggplant30,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: {
    variantColor: theme.colors.rose40,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: {
    variantColor: theme.colors.indigo50,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: {
    variantColor: theme.colors.tangerine30,
  },

  // Deprecated: Emergency (kept for backward compatibility)
  [AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: {
    variantColor: theme.colors.critical30,
  },

  // Vet-specific
  [AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: {
    variantColor: theme.colors.tangerine30,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: {
    variantColor: theme.colors.rose40,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: {
    variantColor: theme.colors.indigo50,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: {
    variantColor: theme.colors.secondary.seaweed30,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: {
    variantColor: theme.colors.secondary.eggplant30,
  },

  // Optometry-specific
  [AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: {
    variantColor: theme.colors.indigo50,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: {
    variantColor: theme.colors.tangerine30,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: {
    variantColor: theme.colors.rose40,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: {
    variantColor: theme.colors.secondary.eggplant30,
  },

  // Default appointment types
  [AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: {
    variantColor: theme.colors.rose40,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: {
    variantColor: theme.colors.indigo50,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: {
    variantColor: theme.colors.tangerine30,
  },

  // Common across all verticals
  [AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: {
    variantColor: theme.colors.secondary.seaweed40,
  },
  [AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: {
    variantColor: theme.colors.neutral30,
  },

  // Categories
  [CategoryEnum.CATEGORY_BILLING]: {
    variantColor: theme.colors.success30,
  },
  [CategoryEnum.CATEGORY_COMPLAINT]: {
    variantColor: theme.colors.warning20,
  },
  [CategoryEnum.CATEGORY_EMERGENCY]: {
    variantColor: theme.colors.critical30,
  },
  [CategoryEnum.CATEGORY_MEDICATION]: {
    variantColor: theme.colors.secondary.eggplant40,
  },
  [CategoryEnum.CATEGORY_OTHER]: {
    variantColor: theme.colors.neutral30,
  },
  [CategoryEnum.CATEGORY_SCHEDULING]: {
    variantColor: theme.colors.primary50,
  },
  [CategoryEnum.CATEGORY_UNKNOWN]: {
    variant: 'neutral',
  },
  [CategoryEnum.CATEGORY_VENDOR]: {
    variantColor: theme.colors.neutral40,
  },

  // Contact Types
  [ContactTypeEnum.CONTACT_EXISTING_PATIENT]: {
    variant: 'primary',
    backgroundColor: theme.colors.primary10,
    textColor: theme.colors.primary60,
  },
  [ContactTypeEnum.CONTACT_NEW_PATIENT]: {
    variant: 'warn',
    backgroundColor: theme.colors.warning10,
    textColor: theme.colors.warning70,
  },
  [ContactTypeEnum.CONTACT_NOT_A_PATIENT]: {
    variant: 'neutral',
    backgroundColor: theme.colors.neutral10,
    textColor: theme.colors.neutral70,
  },
  [ContactTypeEnum.CONTACT_UNKNOWN]: {
    variant: 'neutral',
  },

  // Task Types
  [TaskType.TYPE_BILLING]: {
    variantColor: theme.colors.success30,
  },
  [TaskType.TYPE_INSURANCE]: {
    variantColor: theme.colors.secondary.eggplant60,
  },
  [TaskType.TYPE_OTHER]: {
    variantColor: theme.colors.neutral30,
  },
  [TaskType.TYPE_PATIENT_CARE]: {
    variantColor: theme.colors.warning30,
  },
  [TaskType.TYPE_SCHEDULING]: {
    variantColor: theme.colors.primary50,
  },
  [TaskType.TYPE_WAITLIST]: {
    variantColor: theme.colors.critical50,
  },

  // Sentiment
  [SentimentEnum.SENTIMENT_NEGATIVE]: {
    variant: 'critical',
    leftIconName: 'emoji-sad-small',
  },
  [SentimentEnum.SENTIMENT_NEUTRAL]: {
    variant: 'primary',
    leftIconName: 'emoji-neutral-small',
  },
  [SentimentEnum.SENTIMENT_POSITIVE]: {
    variant: 'success',
    leftIconName: 'emoji-small',
  },
  [SentimentEnum.SENTIMENT_UNKNOWN]: {
    variant: 'neutral',
    leftIconName: 'alert-small',
  },

  //Scheduling Outcome
  [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: {
    variant: 'success',
    leftIconName: 'check-small',
  },

  //Scheduling Outcome V2

  [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: {
    variant: 'primary',
    leftIconName: 'check-small',
    fullWidth: true,
    leftIconColor: theme.colors.primary70,
    textColor: theme.colors.primary70,
  },
  [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: {
    variant: 'warn',
    leftIconName: 'x-small',
    leftIconColor: theme.colors.warning70,
    textColor: theme.colors.warning70,
    backgroundColor: theme.colors.warning10,
    fullWidth: true,
  },
  [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: {
    variant: 'seaweed',
    leftIconName: 'calendar-small',
    leftIconColor: theme.colors.secondary.seaweed70,
    textColor: theme.colors.secondary.seaweed70,
    fullWidth: true,
  },
  [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: {
    variant: 'neutral',
    leftIconName: 'feedback-bad-small',
    leftIconColor: theme.colors.neutral80,
    textColor: theme.colors.neutral70,
    fullWidth: true,
  },

  // Service Quality Flags
  [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: {
    variant: 'default',
    leftIconName: 'wellness-small',
    fullWidth: true,
    textColor: theme.colors.neutral90,
    leftIconColor: theme.colors.tangerine40,
  },
  [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: {
    variant: 'default',
    leftIconName: 'phone-alert-small',
    fullWidth: true,
    textColor: theme.colors.neutral90,
    leftIconColor: theme.colors.rose50,
  },
  [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: {
    variant: 'default',
    leftIconName: 'user-escalation-small',
    fullWidth: true,
    textColor: theme.colors.neutral90,
    leftIconColor: theme.colors.warning30,
  },
};

const DEFAULT_DATA_COLORS: DataLabelsAndColors = {
  appointmentTypes: {
    [AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: theme.colors.secondary.seaweed30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: theme.colors.critical30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: theme.colors.tangerine40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: theme.colors.secondary.eggplant30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: theme.colors.rose40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: theme.colors.tangerine30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: theme.colors.secondary.seaweed40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: theme.colors.neutral20,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: theme.colors.tangerine30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: theme.colors.rose40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: theme.colors.secondary.seaweed30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: theme.colors.secondary.eggplant30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: theme.colors.tangerine30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: theme.colors.rose40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: theme.colors.secondary.eggplant30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: theme.colors.rose40,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: theme.colors.indigo50,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: theme.colors.tangerine30,
    [AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: theme.colors.neutral30,
  },

  categories: {
    [CategoryEnum.CATEGORY_BILLING]: theme.colors.success30,
    [CategoryEnum.CATEGORY_COMPLAINT]: theme.colors.warning20,
    [CategoryEnum.CATEGORY_EMERGENCY]: theme.colors.critical30,
    [CategoryEnum.CATEGORY_MEDICATION]: theme.colors.secondary.eggplant40,
    [CategoryEnum.CATEGORY_OTHER]: theme.colors.neutral30,
    [CategoryEnum.CATEGORY_SCHEDULING]: theme.colors.primary50,
    [CategoryEnum.CATEGORY_UNKNOWN]: theme.colors.neutral20,
    [CategoryEnum.CATEGORY_VENDOR]: theme.colors.neutral40,
  },

  sentiments: {
    [SentimentEnum.SENTIMENT_NEGATIVE]: theme.colors.critical40,
    [SentimentEnum.SENTIMENT_NEUTRAL]: theme.colors.primary20,
    [SentimentEnum.SENTIMENT_POSITIVE]: theme.colors.success30,
    [SentimentEnum.SENTIMENT_UNKNOWN]: theme.colors.neutral30,
  },

  schedulingOutcome: {
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral5,
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: theme.colors.success40,
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning20,
  },

  schedulingOutcomeV2: {
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral50,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: theme.colors.primary40,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning20,
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: theme.colors.secondary.seaweed30,
  },

  serviceQualityFlags: {
    [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: theme.colors.tangerine30,
    [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: theme.colors.rose20,
    [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: theme.colors.warning20,
  },
};

const DEFAULT_DATA_HIGHLIGHT_COLORS: DataLabelsAndColors = {
  serviceQualityFlags: {
    [ServiceQualityFlagEnum.FLAG_EXCELLENT_RESOLUTION]: theme.colors.primary5,
    [ServiceQualityFlagEnum.FLAG_UNRESOLVED_ISSUE]: theme.colors.critical5,
    [ServiceQualityFlagEnum.FLAG_UNKNOWN]: theme.colors.primary5,
  },
};

const DEFAULT_DATA_LABELS = {
  appointmentTypes: {
    [AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: t('Unknown'),
    // Dental-specific
    [AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: t('Imaging'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: t('Cosmetic'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: t('Orthodontics'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: t('Preventive'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: t('Hygiene'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: t('Restorative'),
    // Deprecated: Emergency (kept for backward compatibility)
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: t('Emergency'),

    // Vet-specific
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: t('Sick Pet'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: t('Preventive Care'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: t('Dental'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: t('Diagnostics'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: t('End of Life Care'),

    // Optometry-specific
    [AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: t('Eye Exam'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: t('Vision Correction'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: t('Medical Eye Care'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: t('Specialty Services'),

    // Default appointment types
    [AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: t('Consultation'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: t('Routine Checkup'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: t('Diagnostic Tests'),

    // Common across all verticals
    [AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: t('Surgery'),
    [AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: t('Other'),
  },

  categories: {
    [CategoryEnum.CATEGORY_BILLING]: t('Billing'),
    [CategoryEnum.CATEGORY_COMPLAINT]: t('Complaint'),
    [CategoryEnum.CATEGORY_EMERGENCY]: t('Emergency'),
    [CategoryEnum.CATEGORY_MEDICATION]: t('Medication'),
    [CategoryEnum.CATEGORY_OTHER]: t('Other'),
    [CategoryEnum.CATEGORY_SCHEDULING]: t('Scheduling'),
    [CategoryEnum.CATEGORY_UNKNOWN]: t('Unknown'),
    [CategoryEnum.CATEGORY_VENDOR]: t('Vendor'),
  },

  sentiments: {
    [SentimentEnum.SENTIMENT_NEGATIVE]: t('Unhappy'),
    [SentimentEnum.SENTIMENT_NEUTRAL]: t('Neutral'),
    [SentimentEnum.SENTIMENT_POSITIVE]: t('Happy'),
    [SentimentEnum.SENTIMENT_UNKNOWN]: t('Unknown'),
  },

  sentimentsWithEmoji: {
    [SentimentEnum.SENTIMENT_NEGATIVE]: `😒 ${t('Unhappy')}`,
    [SentimentEnum.SENTIMENT_NEUTRAL]: `😐 ${t('Neutral')}`,
    [SentimentEnum.SENTIMENT_POSITIVE]: `😊 ${t('Happy')}`,
    [SentimentEnum.SENTIMENT_UNKNOWN]: `🤔 ${t('Unknown')}`,
  },

  serviceQualityFlags: {
    [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: t('Treatment Concern'),
    [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: t('Unresolved Contact Attempt'),
    [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: t('Manager Escalation'),
  },

  serviceQualityLabels: {
    [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: t('Treatment Concerns'),
    [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: t('Unresolved Contact Attempts'),
    [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: t('Manager Escalations'),
  },

  taskTypes: {
    [TaskType.TYPE_BILLING]: t('Billing'),
    [TaskType.TYPE_INSURANCE]: t('Insurance'),
    [TaskType.TYPE_OTHER]: t('Other'),
    [TaskType.TYPE_PATIENT_CARE]: t('Patient Care'),
    [TaskType.TYPE_SCHEDULING]: t('Scheduling'),
    [TaskType.TYPE_WAITLIST]: t('Waitlist'),
  },

  schedulingOutcome: {
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: t('Scheduled'), //TODO: Remove this and use the V2 version instead after GA
    [SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Unscheduled'), //TODO: Remove this and use the V2 version instead after GA
  },

  schedulingOutcomeV2: {
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: t('Scheduled On Call'),
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Unscheduled'),
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: t('Recovered Opportunity'),
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: t('No Opportunity'),
  },

  schedulingOutcomeV2Alternate: {
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: t('Scheduled On Call'),
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Still Unscheduled'),
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: t('Recovered Opportunities'),
    [SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: t('No Opportunity'),
  },
};

const DEFAULT_HEADER_CHIP_VARIANTS: CallIntelChipVariant = {
  // Task Types
  [TaskType.TYPE_BILLING]: {
    variant: 'success',
    variantColor: theme.colors.success5,
    textColor: theme.colors.success60,
  },
  [TaskType.TYPE_INSURANCE]: {
    variant: 'eggplant',
    variantColor: theme.colors.secondary.eggplant5,
    textColor: theme.colors.secondary.eggplant60,
  },
  [TaskType.TYPE_OTHER]: {
    variant: 'neutral',
    variantColor: theme.colors.neutral5,
    textColor: theme.colors.neutral60,
  },
  [TaskType.TYPE_PATIENT_CARE]: {
    variant: 'warn',
    variantColor: theme.colors.warning5,
    textColor: theme.colors.warning60,
  },
  [TaskType.TYPE_SCHEDULING]: {
    variant: 'primary',
    variantColor: theme.colors.primary5,
    textColor: theme.colors.primary70,
  },
  [TaskType.TYPE_WAITLIST]: {
    variant: 'critical',
    variantColor: theme.colors.critical5,
    textColor: theme.colors.critical60,
  },
};

const DEFAULT_PAGE_LABELS: PageLabels = {
  serviceQualityFlags: {
    [ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: t('Treatment Concerns'),
    [ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: t('Unresolved Contact Attempts'),
    [ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: t('Manager Escalations'),
  },
};

interface CallIntelDefaultsStore {
  availableAppointmentTypes: AppointmentTypeEnum[];
  availableCategories: CategoryEnum[];
  chartLabelColors: DataLabelsAndColors;
  chartOutlineColors: DataLabelsAndColors;
  chipVariants: CallIntelChipVariant;
  dataColors: DataLabelsAndColors;
  dataHighlightColors: DataLabelsAndColors;
  dataLabels: DataLabels;
  headerChipVariants: CallIntelChipVariant;
  pageLabels: PageLabels;
  descriptionLabelsMap: DescriptionLabelsMapType;

  setAvailableAppointmentTypes: (availableAppointmentTypes: AppointmentTypeEnum[]) => void;
  setAvailableCategories: (availableCategories: CategoryEnum[]) => void;
  setChartLabelColors: (chartLabelColors: DataLabelsAndColors) => void;
  setChartOutlineColors: (chartOutlineColors: DataLabelsAndColors) => void;
  setChipVariants: (chipVariants: CallIntelChipVariant) => void;
  setDataColors: (dataColors: DataLabelsAndColors) => void;
  setDataHighlightColors: (dataHighlightColors: DataLabelsAndColors) => void;
  setHeaderChipVariants: (headerChipVariants: CallIntelChipVariant) => void;
  setPageLabels: (pageLabels: PageLabels) => void;
}

const useCallIntelDefaultsStore = createStoreWithSubscribe<CallIntelDefaultsStore>(
  (set) => ({
    availableAppointmentTypes: DEFAULT_APPOINTMENT_TYPES,
    availableCategories: DEFAULT_CATEGORIES,
    chartLabelColors: DEFAULT_CHART_LABEL_COLORS,
    chartOutlineColors: DEFAULT_CHART_OUTLINE_COLORS,
    chipVariants: DEFAULT_CHIP_VARIANTS,
    dataColors: DEFAULT_DATA_COLORS,
    dataHighlightColors: DEFAULT_DATA_HIGHLIGHT_COLORS,
    dataLabels: DEFAULT_DATA_LABELS,
    descriptionLabelsMap: DEFAULT_DESCRIPTION_LABELS,
    headerChipVariants: DEFAULT_HEADER_CHIP_VARIANTS,
    pageLabels: DEFAULT_PAGE_LABELS,

    setAvailableAppointmentTypes: (availableAppointmentTypes) => set({ availableAppointmentTypes }),
    setAvailableCategories: (availableCategories) => set({ availableCategories }),
    setChartLabelColors: (chartLabelColors) => set({ chartLabelColors }),
    setChartOutlineColors: (chartOutlineColors) => set({ chartOutlineColors }),
    setChipVariants: (chipVariants) => set({ chipVariants }),
    setDataColors: (dataColors) => set({ dataColors }),
    setDataHighlightColors: (dataHighlightColors) => set({ dataHighlightColors }),
    setHeaderChipVariants: (headerChipVariants) => set({ headerChipVariants }),
    setPageLabels: (pageLabels) => set({ pageLabels }),
  }),
  { name: 'CallIntelDefaultsStore', trace: true }
);

export const useCallIntelContactTypesLabel = () => {
  const { t } = useTranslation('analytics');
  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();

  return useMemo<Record<ContactTypeEnum, string>>(() => {
    return {
      [ContactTypeEnum.CONTACT_EXISTING_PATIENT]: t('Existing {{patientCapitalizedToken}}', {
        patientCapitalizedToken,
      }),
      [ContactTypeEnum.CONTACT_NEW_PATIENT]: t('New {{patientCapitalizedToken}}', {
        patientCapitalizedToken,
      }),
      [ContactTypeEnum.CONTACT_NOT_A_PATIENT]: t('Not a {{patientCapitalizedToken}}', {
        patientCapitalizedToken,
      }),
      [ContactTypeEnum.CONTACT_UNKNOWN]: t('Unavailable Data'),
    };
  }, [t, patientCapitalizedToken]);
};

export const useCallIntelFeedbackFeatureLabels = () => {
  const { t } = useTranslation('call-intelligence');

  return useMemo<Record<FeedbackFeatureType, string>>(
    () => ({
      [FeedbackFeature.FEATURE_CALL_INTELLIGENCE]: t('Call Intelligence'),
      [FeedbackFeature.FEATURE_AI_RECEPTIONIST]: t('AI Receptionist'),
      [FeedbackFeature.FEATURE_NETWORK_PERFORMANCE]: t('Network Performance'),
      [FeedbackFeature.FEATURE_OTHER]: t('Other'),
    }),
    [t]
  );
};

export const useCallIntelFeedbackSubIssueLabels = () => {
  const { t } = useTranslation('call-intelligence');

  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();

  return useMemo<Partial<Record<FeedbackSubIssueEnum, string>>>(() => {
    return {
      [FeedbackSubIssueEnum.SUB_ISSUE_CALL_SUMMARY_LOW_PRECISION]: t('Summary missed key details'),
      [FeedbackSubIssueEnum.SUB_ISSUE_CALL_TRANSCRIPT_LOW_PRECISION]: t('Transcript has mistakes'),
      [FeedbackSubIssueEnum.SUB_ISSUE_APPOINTMENT_TYPE_MISLABELLED]: t('Appointment Type is incorrect'),
      [FeedbackSubIssueEnum.SUB_ISSUE_CALL_CATEGORY_MISLABELLED]: t('Call Category is incorrect'),
      [FeedbackSubIssueEnum.SUB_ISSUE_CALL_SENTIMENT_MISLABELLED]: t("Customer Sentiment doesn't feel accurate"),
      [FeedbackSubIssueEnum.SUB_ISSUE_CONTACT_TYPE_MISLABELLED]: t(
        '{{patientCapitalizedToken}} was mislabeled as New or Existing',
        { patientCapitalizedToken }
      ),
      [FeedbackSubIssueEnum.SUB_ISSUE_SCHEDULING_OUTCOME_MISLABELLED]: t(
        "Scheduling Outcome doesn't match what happened"
      ),
      [FeedbackSubIssueEnum.SUB_ISSUE_SERVICE_ISSUES_MISLABELLED]: t("Service Issue doesn't apply to this call"),
      [FeedbackSubIssueEnum.SUB_ISSUE_FOLLOWUP_TASKS_UNNECESSARY]: t('Follow-up Tasks are unnecessary'),
      [FeedbackSubIssueEnum.SUB_ISSUE_OTHER]: t('Other'),
    };
  }, [t, patientCapitalizedToken]);
};

export const useCallIntelDefaultsShallowStore = createShallowStore<CallIntelDefaultsStore>(useCallIntelDefaultsStore);
